/**
 * Media helpers shared by the library page, the picker, editors and board
 * views: URLs, upload with progress, preloading, formatting.
 */
import { MEDIA_LIMITS, type MediaInfo, type MediaKind } from '@schulspiele/protocol';

/** `phone`: a smaller image for phones (Bildpunkt). */
export type MediaVariant = 'original' | 'thumb' | 'poster' | 'phone';

/**
 * URL of a medium. With a session's media key it works on the board (no
 * login there); without, only with the teacher's cookie.
 */
export function mediaUrl(
  id: string,
  variant: MediaVariant = 'original',
  mediaKey?: string | null,
): string {
  const path = `/media/${encodeURIComponent(id)}${variant === 'original' ? '' : `/${variant}`}`;
  return mediaKey != null && mediaKey !== '' ? `${path}?k=${encodeURIComponent(mediaKey)}` : path;
}

export class UploadError extends Error {
  public constructor(
    public readonly status: number,
    reason: string,
  ) {
    super(reason);
    this.name = 'UploadError';
  }
}

/**
 * Upload one file. Uses XMLHttpRequest because fetch cannot report upload
 * progress. `onProgress` gets 0 to 1. Rejects with `UploadError` carrying
 * the server's German reason.
 */
export function uploadMedia(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<MediaInfo> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/media');
    xhr.withCredentials = true;
    xhr.responseType = 'json';
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    });
    xhr.addEventListener('load', () => {
      const body = xhr.response as (MediaInfo & { reason?: string }) | null;
      if (xhr.status === 201 && body !== null) {
        onProgress?.(1);
        resolve(body);
      } else {
        reject(new UploadError(xhr.status, body?.reason ?? 'Hochladen fehlgeschlagen.'));
      }
    });
    xhr.addEventListener('error', () => {
      reject(new UploadError(0, 'Keine Verbindung zum Server.'));
    });
    const form = new FormData();
    form.append('file', file, file.name);
    xhr.send(form);
  });
}

/** Rough client-side pre-check so obviously too large files fail before uploading. */
export function guessKind(file: File): MediaKind | null {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type.startsWith('video/')) return 'video';
  return null;
}

export function tooLarge(file: File): boolean {
  const kind = guessKind(file);
  return kind !== null && file.size > MEDIA_LIMITS[kind];
}

/**
 * Fetch media into the browser cache before they are needed, so revealing
 * a question shows its picture or plays its sound instantly. Resolves when
 * all are done (failures count as done; the board then loads on demand).
 */
export async function preloadMedia(
  urls: string[],
  onProgress?: (done: number, total: number) => void,
): Promise<void> {
  const unique = [...new Set(urls)];
  let done = 0;
  onProgress?.(0, unique.length);
  await Promise.all(
    unique.map(async (url) => {
      try {
        const res = await fetch(url, { credentials: 'same-origin' });
        await res.blob();
      } catch {
        // Loaded on demand later.
      }
      done += 1;
      onProgress?.(done, unique.length);
    }),
  );
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

export function formatDuration(ms: number | null): string {
  if (ms === null) return '';
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m)}:${String(s).padStart(2, '0')}`;
}

export const KIND_LABEL: Record<MediaKind, string> = {
  image: 'Bild',
  audio: 'Ton',
  video: 'Video',
};
